import { BlogPost } from "@/types/blog";

export const post: BlogPost = {
  slug: "introducing-fordjes-new-developer-platform",
  title: "Introducing Fordje's New Developer Platform",
  excerpt: "Discover how Fordje's new developer platform helps construction teams navigate city code requirements with ease and accuracy.",
  content: `
    <h2>Finding Accurate City Code Information is a Struggle</h2>
    
    <p>
      At Fordje, we understand that navigating city code requirements can often feel like piecing together a challenging puzzle. 
      Whether you're designing your project, applying for permits, or getting inspections done, correct information is the key to 
      fast turnaround. That's why we're thrilled to announce the launch of our new developer platform — a game-changer for 
      construction teams looking to streamline processes and unlock growth opportunities.
    </p>
    
    <h2>Equip Your Team for Success with Fordje</h2>
    
    <p>
      Our platform is designed to empower your team with the tools they need to ensure city code and zoning requirements are right 
      the first time. No more wasted hours piecing together fragmented information or managing unexpected permit rejections when 
      codes change. With Fordje, you can confidently integrate up-to-date code requirements into your workflow and take on projects 
      with greater efficiency and accuracy.
    </p>
    
    <div class="mb-8 overflow-hidden rounded-lg">
      <img 
        src="/lovable-uploads/57880e0a-aee0-41fa-bff6-19574cac68ec.png" 
        alt="Fordje Developer Platform showing a data table with city code information"
        class="w-full object-cover"
      />
    </div>
    
    <h2>What Makes Fordje's Developer Platform Stand Out?</h2>
    
    <h3>Synthesize Critical Data with Ease</h3>
    
    <p>
      City codes are ever changing and differ from city to city. And requirement needs vary across design, engineering, permitting, 
      and inspection stages. Rather than dealing with permit rejections to learn about code changes, or rely on never ending research, 
      Fordje makes it seamless:
    </p>
    
    <ul>
      <li>
        <strong>Customizable Data Selection:</strong> Select the cities and data points that matter most to your project—whether it's zoning, 
        building codes, or even climate requirements. Fordje's AI scans local and state codes to deliver the correct data each time.
      </li>
      <li>
        <strong>Downloadable Tables:</strong> Fordje delivers the data in a downloadable CSV so you can integrate it quickly into your project 
        management tools or workflows.
      </li>
      <li>
        <strong>Need Additional Codes?</strong> If you need additional codes that aren't available in the platform, simply make a request in the platform. 
        And if its urgent, utilize our Research tool to find the information you need within a few minutes of querying.
      </li>
    </ul>
    
    <h3>Accelerated Research for Confident Expansion</h3>
    
    <p>
      With our robust research tools, your team can explore new markets faster and with greater confidence:
    </p>
    
    <ul>
      <li>
        <strong>5x Faster Research:</strong> Research across new cities and counties with speed to easily determine applicable requirements to your company. 
        Minimize time spent gathering city code data and maximize time spent on execution.
      </li>
      <li>
        Gain clarity on expansion opportunities, existing codes and gaps, and empower your team to grow your footprint efficiently and accurately.
      </li>
    </ul>
    
    <h2>How We Keep City Code Data Reliable and Up-to-Date</h2>
    
    <p>
      Fordje combines cutting-edge technology with a human touch to deliver reliable city code data you can trust:
    </p>
    
    <ul>
      <li>
        <strong>Direct Collaboration with Cities:</strong> We work closely with municipalities to ensure our code information is always accurate and up-to-date. 
        This eliminates the need for initial calls or meetings, so your team can focus on finalizing plans. And cities can spend their time focusing on 
        finalized plans, rather than constantly answering the same questions.
      </li>
      <li>
        <strong>Expert-Verified Data:</strong> To fill gaps, Fordje can research and ingest information from cities that aren't yet participating in our platform. 
        Our experts then review and verify the information to maintain the highest standards of accuracy.
      </li>
    </ul>
    
    <h2>Experience Fordje Today</h2>
    
    <p>
      Fordje's developer platform is here to revolutionize how your construction team navigates city code requirements. Save time, reduce errors, 
      and unlock growth opportunities like never before.
    </p>
    
    <p>
      Want to see how it works? Reach out to schedule a demo today and equip your team with the tools they need to thrive!
    </p>
  `,
  coverImage: "/lovable-uploads/57880e0a-aee0-41fa-bff6-19574cac68ec.png",
  date: "2024-12-01T12:00:00",
  readTime: 5,
  categories: ["AI", "Product Release"],
  author: {
    name: "Gillian (Wildfire) Sowray",
    title: "Product Manager",
    avatar: "/lovable-uploads/69c2731c-f0dc-41d3-8812-8b392d7302c2.png"
  }
};
